import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';

/** Matches GET /api/alerts JSON shape. */
export interface ActiveAlert {
  id: number;
  message: string;
  link: string | null;
  level: string;
}

const CACHE_KEY = 'devex-alert-cache';
const DISMISSED_KEY = 'devex-alert-dismissed-id';

const ALERTS_ENDPOINT = '/api/alerts';

function activeAlertQueryOptions(getToken: () => Promise<string>) {
  return {
    queryKey: ['alerts', 'active'] as const,
    queryFn: () => fetchApi<ActiveAlert | null>(ALERTS_ENDPOINT, getToken),
    staleTime: Infinity,
    retry: 1,
  };
}

/** Raw active alert from the API (shared cache with {@link useActiveAlert}). */
export function useActiveAlertQuery() {
  const getToken = useAccessToken();
  return useQuery<ActiveAlert | null>(activeAlertQueryOptions(getToken));
}

function parseAlert(raw: unknown): ActiveAlert | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Partial<ActiveAlert>;
  if (typeof o.id !== 'number' || typeof o.message !== 'string') return null;
  return {
    id: o.id,
    message: o.message,
    link: o.link ?? null,
    level: typeof o.level === 'string' ? o.level : 'info',
  };
}

function readCachedAlert(): ActiveAlert | null {
  try {
    const s = localStorage.getItem(CACHE_KEY);
    if (!s) return null;
    return parseAlert(JSON.parse(s));
  } catch {
    return null;
  }
}

function writeCachedAlert(alert: ActiveAlert | null): void {
  if (alert == null) localStorage.removeItem(CACHE_KEY);
  else localStorage.setItem(CACHE_KEY, JSON.stringify(alert));
}

function readDismissedId(): string | null {
  return localStorage.getItem(DISMISSED_KEY);
}

function writeDismissedId(id: string | null): void {
  if (id == null) localStorage.removeItem(DISMISSED_KEY);
  else localStorage.setItem(DISMISSED_KEY, id);
}

export interface AlertMutationPayload {
  message: string;
  level: string;
  link: string;
}

export function useAlertMutations(
  payload: AlertMutationPayload,
  callbacks: {
    onSuccess: () => void;
    onDisableSuccess: () => void;
    onError: (message: string) => void;
  }
) {
  const getToken = useAccessToken();
  const queryClient = useQueryClient();

  const invalidate = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: ['alerts', 'active'] }),
    [queryClient]
  );

  const patchBody = (active: boolean) => ({
    message: payload.message.trim(),
    level: payload.level,
    link: payload.link.trim() === '' ? null : payload.link.trim(),
    active,
  });

  const publishMutation = useMutation({
    mutationFn: () =>
      fetchApi<ActiveAlert>('/api/alerts', getToken, {
        method: 'POST',
        body: JSON.stringify({
          message: payload.message.trim(),
          level: payload.level,
          link: payload.link.trim() === '' ? null : payload.link.trim(),
        }),
      }),
    onSuccess: () => {
      invalidate();
      callbacks.onSuccess();
    },
    onError: (e: Error) => callbacks.onError(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: (id: number) =>
      fetchApi<ActiveAlert>(`/api/alerts/${id}`, getToken, {
        method: 'PATCH',
        body: JSON.stringify(patchBody(true)),
      }),
    onSuccess: () => {
      invalidate();
      callbacks.onSuccess();
    },
    onError: (e: Error) => callbacks.onError(e.message),
  });

  const disableMutation = useMutation({
    mutationFn: (id: number) =>
      fetchApi<ActiveAlert>(`/api/alerts/${id}`, getToken, {
        method: 'PATCH',
        body: JSON.stringify(patchBody(false)),
      }),
    onSuccess: () => {
      invalidate();
      callbacks.onDisableSuccess();
    },
    onError: (e: Error) => callbacks.onError(e.message),
  });

  return { publishMutation, updateMutation, disableMutation };
}

export function useActiveAlert() {
  const getToken = useAccessToken();
  const [dismissedId, setDismissedId] = useState<string | null>(() => readDismissedId());
  const [cachedAtMount] = useState(() => readCachedAlert());
  const query = useQuery<ActiveAlert | null>(activeAlertQueryOptions(getToken));

  useEffect(() => {
    if (!query.isSuccess) return;
    if (query.data == null) {
      writeCachedAlert(null);
      writeDismissedId(null);
      setDismissedId(null);
      return;
    }
    writeCachedAlert(query.data);
  }, [query.isSuccess, query.data]);

  const alert = useMemo((): ActiveAlert | null => {
    const dismissed = dismissedId;
    if (query.isSuccess) {
      const data = query.data;
      if (data == null) return null;
      if (dismissed !== null && String(data.id) === dismissed) return null;
      return data;
    }
    if (cachedAtMount == null) return null;
    if (dismissed !== null && String(cachedAtMount.id) === dismissed) return null;
    return cachedAtMount;
  }, [query.isSuccess, query.data, dismissedId, cachedAtMount]);

  const dismiss = useCallback(() => {
    if (alert == null) return;
    const id = String(alert.id);
    writeDismissedId(id);
    setDismissedId(id);
  }, [alert]);

  return {
    alert,
    dismiss,
    isLoading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
  };
}
